import { FC } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Button } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import { dcaHotkeysHelpOpenAtom, dcaHotkeysModeAtom } from './atoms';
import styles from './hotkeys-help-overlay.module.scss';

type Row = { keys: string; behavior: string };

/** Keys that work the same way in both mappings. */
const SHARED_ROWS: Row[] = [
  { keys: 'Ctrl/Cmd + K', behavior: 'Enter hotkeys mode' },
  { keys: 'Esc', behavior: 'Leave hotkeys mode' },
  { keys: 'Ctrl/Cmd + H', behavior: 'Show or hide this cheatsheet' },
  { keys: 'Ctrl/Cmd + I', behavior: 'Switch to the Insights mapping' },
  { keys: 'Ctrl/Cmd + A', behavior: 'Switch to the ARIES mapping' },
  { keys: 'Shift + Left / Right', behavior: 'Previous / next well' },
  {
    keys: 'Shift + Up / Down',
    behavior: 'Previous / next stream (oil, gas, water)',
  },
  { keys: 'Shift + S', behavior: 'Check the selected stream for regression' },
  { keys: 'Shift + F', behavior: 'Fit the checked streams' },
  {
    keys: 'Ctrl/Cmd + E',
    behavior: 'Show only the selected stream, or all of them again',
  },
  {
    keys: 'Alt + Left / Right (Cmd + Left / Right on Mac)',
    behavior: 'Shift the start date one month',
  },
  { keys: 'Ctrl/Cmd + S', behavior: 'Save the DCA forecast' },
];

/** Parameter keys, one list per mapping. */
const PARAMETER_ROWS: Record<'insights' | 'aries', Row[]> = {
  insights: [
    { keys: 'Up / Down', behavior: 'Ramp up qi' },
    {
      keys: 'Left / Right',
      behavior: 'Ramp up duration (t_elf on a ratio model)',
    },
    { keys: '7 / 1', behavior: 'Ramp up n' },
    { keys: '8 / 2', behavior: 'qi (Ri on a ratio model)' },
    { keys: '6 / 4', behavior: 'Di' },
    { keys: '+ / -', behavior: 'b-factor' },
    { keys: '9 / 3', behavior: 'Dlim' },
  ],
  aries: [
    { keys: 'Up / Down', behavior: 'qi (Ri on a ratio model)' },
    { keys: 'Left / Right', behavior: 'Di' },
    { keys: '+ / -', behavior: 'b-factor' },
  ],
};

const MAPPING_LABELS: Record<'insights' | 'aries', string> = {
  insights: 'Insights',
  aries: 'ARIES',
};

/**
 * Renders one list of key/behavior pairs.
 *
 * @param {object} props - The rows to list.
 * @return {JSX.Element} A definition list of the rows.
 */
const HotkeyRows: FC<{ rows: Row[] }> = ({ rows }) => (
  <dl className={styles.rows}>
    {rows.map(({ keys, behavior }) => (
      <div key={keys} className={styles.row}>
        <dt className={styles.keys}>{keys}</dt>
        <dd className={styles.behavior}>{behavior}</dd>
      </div>
    ))}
  </dl>
);

/**
 * Cheatsheet for hotkeys mode, opened with Ctrl/Cmd+H. Lists the shared keys
 * plus the parameter keys for the mapping that is currently selected.
 *
 * @return {JSX.Element | null} The overlay, or nothing when it is closed.
 */
export const HotkeysHelpOverlay: FC = () => {
  const [open, setOpen] = useAtom(dcaHotkeysHelpOpenAtom);
  const { mapping } = useAtomValue(dcaHotkeysModeAtom);

  if (!open) return null;

  return (
    <div className={styles.backdrop} onClick={() => setOpen(false)}>
      <div
        className={styles.panel}
        role="dialog"
        aria-label="DCA hotkeys"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <h2 className={styles.title}>
            {`Hotkeys: ${MAPPING_LABELS[mapping]} mapping`}
          </h2>
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={Close}
            iconDescription="Close"
            tooltipPosition="left"
            onClick={() => setOpen(false)}
          />
        </div>
        <div className={styles.columns}>
          <section>
            <h3 className={styles.sectionTitle}>Mode and navigation</h3>
            <HotkeyRows rows={SHARED_ROWS} />
          </section>
          <section>
            <h3 className={styles.sectionTitle}>Parameters</h3>
            <HotkeyRows rows={PARAMETER_ROWS[mapping]} />
          </section>
        </div>
      </div>
    </div>
  );
};
